import { round6, round8 } from 'utilities/math';
import {
  mean,
  sampleStandardDeviation
} from 'simple-statistics';

import isEmpty from 'lodash/isEmpty';

import {
  GREATER_THAN_VALUE,
  LESS_THAN_VALUE
} from 'scenes/InferentialStatistics/Constants';
import {
  makeTStat,
  makeStandardError,
  makeOneSidedPValue,
  makeTwoSidedPValue,
  makeTConfidenceInterval
} from './oneSampleT';

const makePValue = (
  alternativeHypothesis,
  tStatisticsWithProperSign,
  sampleSize
) => {
  let pValue = 0;
  switch (alternativeHypothesis) {
    case GREATER_THAN_VALUE:
      tStatisticsWithProperSign > 0
        ? (pValue = makeOneSidedPValue(tStatisticsWithProperSign, +sampleSize))
        : (pValue = 1 - makeOneSidedPValue(tStatisticsWithProperSign, +sampleSize));
      break;
    case LESS_THAN_VALUE:
      tStatisticsWithProperSign < 0
        ? (pValue = makeOneSidedPValue(tStatisticsWithProperSign, +sampleSize))
        : (pValue = 1 - makeOneSidedPValue(tStatisticsWithProperSign, +sampleSize));
      break;

    default:
      pValue = makeTwoSidedPValue(tStatisticsWithProperSign, +sampleSize);
  }
  return pValue;
};

export const makePairedTHypothesizedProportion = ({
  sampleMeanDiff,
  standardDeviationDiff,
  sampleSize,
  hypothesisMean,
  alternativeHypothesis
}) => {
  const df = +sampleSize - 1;
  const tStatistics = makeTStat(
    +hypothesisMean,
    +sampleMeanDiff,
    +standardDeviationDiff,
    +sampleSize
  );
  const tStatisticsWithProperSign = Math.sign(tStatistics) * (-Math.abs(tStatistics));

  const pValue = makePValue(alternativeHypothesis, tStatisticsWithProperSign, +sampleSize);

  return {
    h0: hypothesisMean,
    h1: hypothesisMean,
    df,
    tStatistics: tStatisticsWithProperSign,
    pValue
  };
};

export const makeDiffVariable = (first, second) =>
  first.filter((item, i) => {
    return item !== 'empty' && second[i] !== 'empty';
  }).map((item, i) => round8(item - second[i]));

const makeMeanOfSampleDiff = diffVariable =>
  diffVariable.reduce((sum, item) => sum + item, 0) / diffVariable.length;

const makeSampleStandardDeviationForTwoGroup = (
  diffVariables,
  meanOfSampleDiff
) => {
  const diff = diffVariables.reduce((sum, item) => {
    return sum + (item - meanOfSampleDiff) ** 2;
  }, 0);
  return Math.sqrt(diff / (diffVariables.length - 1));
};

export const makePairedTHypothesizedProportionForTwoGroup = ({
  hypothesisMean,
  firstVariable,
  secondVariable,
  alternativeHypothesis
}) => {
  const n = firstVariable.length;
  const df = n - 1;
  const diffVariable = makeDiffVariable(firstVariable, secondVariable);
  const meanOfSampleDiff = makeMeanOfSampleDiff(diffVariable);

  const standardDeviation = makeSampleStandardDeviationForTwoGroup(diffVariable, meanOfSampleDiff);

  const standardError = makeStandardError(standardDeviation, n);

  const tStatistics = makeTStat(
    meanOfSampleDiff,
    +hypothesisMean,
    standardDeviation,
    n
  );

  const pValue = makePValue(
    alternativeHypothesis,
    tStatistics,
    n
  );

  return {
    h0: hypothesisMean,
    h1: hypothesisMean,
    df,
    pValue,
    tStatistics: tStatistics ? tStatistics : 0,
    standardError,
    meanOfSampleDiff
  };
};

const getConfidenceLimits = ({
  sampleMean,
  confidenceLevel,
  stDev,
  df
}) => {
  const [lowerLimit, upperLimit] = makeTConfidenceInterval(
    sampleMean,
    +confidenceLevel,
    stDev,
    df + 1
  );
  return {
    df,
    lowerLimit,
    upperLimit
  };
};

export const makePairedTConfidenceLimits = ({
  sampleMeanDiff,
  standardDeviationDiff,
  sampleSize,
  confidenceInterval
}) => {
  const df = sampleSize - 1;
  const confidenceLevel = 1 - (+confidenceInterval / 100);
  return getConfidenceLimits({
    sampleMean: +sampleMeanDiff,
    confidenceLevel,
    stDev: standardDeviationDiff,
    df
  });
};

export const makePairedTConfidenceLimitsForTwoGroup = ({
  confidenceInterval,
  firstVariable,
  secondVariable
}) => {
  const confidenceLevel = 1 - (+confidenceInterval / 100);
  const diffVariable = makeDiffVariable(firstVariable, secondVariable);
  const n = diffVariable.length;
  const df = n - 1;
  const meanOfSampleDiff = round6(makeMeanOfSampleDiff(diffVariable));
  const stDev = round6(makeSampleStandardDeviationForTwoGroup(diffVariable, meanOfSampleDiff));
  const standardError = makeStandardError(stDev, n);

  const limits = getConfidenceLimits({
    sampleMean: meanOfSampleDiff,
    confidenceLevel,
    stDev,
    df
  });
  return {
    ...limits,
    meanOfSampleDiff,
    standardError
  };
};

export const calculateTableDataForPairedT = ({
  sampleMeanDiff,
  standardDeviationDiff,
  sampleSize,
  datasetContains,
  numericalRowDataSet
}, hasDataSetOneVariable) => {
  let headersList = [];
  if ((datasetContains === 'differenceData' || hasDataSetOneVariable) && numericalRowDataSet) {
    headersList = [numericalRowDataSet];
  }

  return ([
    [...headersList,
      sampleSize,
      round6(sampleMeanDiff),
      round6(standardDeviationDiff),
      round6(makeStandardError(+standardDeviationDiff, +sampleSize))
    ]
  ]);
};

export const calculateTableDataForPairedTForTwoGroups = ({
  numericalRowDataSetFirst,
  numericalRowDataSetSecond,
  sampleSizeFirst,
  sampleMeanFirst,
  sampleMeanSecond,
  standardDeviationFirst,
  standardDeviationSecond
}) => {
  const stErrorFirst = round6(makeStandardError(standardDeviationFirst, sampleSizeFirst));
  const stErrorSecond = round6(makeStandardError(standardDeviationSecond, sampleSizeFirst));

  return [
    [numericalRowDataSetFirst, sampleSizeFirst, round6(sampleMeanFirst), round6(standardDeviationFirst), stErrorFirst],
    [numericalRowDataSetSecond, sampleSizeFirst, round6(sampleMeanSecond), round6(standardDeviationSecond), stErrorSecond]
  ];
};

export const calculateSummaryVariable = (firstVariable, secondVariable) => {
  return ({
    sampleMeanFirst: round6(mean(firstVariable)),
    sampleSizeFirst: firstVariable.length,
    standardDeviationFirst: round6(sampleStandardDeviation(firstVariable)),

    sampleMeanSecond: round6(mean(secondVariable)),
    sampleSizeSecond: secondVariable.length,
    standardDeviationSecond: round6(sampleStandardDeviation(secondVariable))
  });
};

export const getSummaryVariable = (firstSummary, secondSummary) => {
  let sampleMeanSecond = 'N/A';
  let sampleSizeSecond = 'N/A';
  let standardDeviationSecond = 'N/A';

  if (!isEmpty(secondSummary)) {
    sampleMeanSecond = secondSummary.mean;
    sampleSizeSecond = secondSummary.count;
    standardDeviationSecond = secondSummary.sampleStandardDeviation;
  }

  return ({
    sampleMeanFirst: firstSummary.mean,
    sampleSizeFirst: firstSummary.count,
    standardDeviationFirst: firstSummary.sampleStandardDeviation,
    sampleMeanSecond,
    sampleSizeSecond,
    standardDeviationSecond
  });
};

export const calculateDefaultValueForPairedT = ({
  numericalSummary,
  numericalRowDataSet,
  firstAndSecondNumericalVariable,
  numericalSelectedParametersNames
}) => {
  if (numericalRowDataSet) {
    const valueDataSet = numericalSummary[numericalRowDataSet];
    return ({
      numericalRowDataSet,
      sampleSize: valueDataSet.count,
      sampleMeanDiff: round6(valueDataSet.mean),
      standardDeviationDiff: round6(valueDataSet.sampleStandardDeviation)
    });
  } else {
    const firstNumericalSummary = numericalSummary[numericalSelectedParametersNames[0]];

    const numericalRowDataSetFirst = firstNumericalSummary.name;
    const secondNumericalSummary = numericalSummary[numericalSelectedParametersNames[1]];
    const numericalRowDataSetSecond = secondNumericalSummary.name;
    let summary = {};

    const { firstParsedParameterValues, secondParsedParameterValues } = firstAndSecondNumericalVariable;
    summary = calculateSummaryVariable(firstParsedParameterValues, secondParsedParameterValues);

    return ({
      numericalRowDataSetFirst,
      numericalRowDataSetSecond,
      ...summary
    });
  }
};
